import {
  GLOBAL_LISTING_TYPE_OPTIONS,
  GLOBAL_PROPERTY_TYPE_OPTIONS,
  GLOBAL_TIME_RANGE_OPTIONS,
} from "@repo/adapter-core";
import { JOB_CONFIG_DEFAULTS as D } from "./schema.ts";

/** Textarea placeholder for "Describe the job" — at most six lines. */
export const JOB_PROMPT_PLACEHOLDER = [
  "Describe the job, e.g. “Sold houses in Brighton 3186 VIC from Domain, last 6 months, run now”. Say:",
  "• Suburbs with postcode + state, or the name of a saved suburb list",
  "• Which sites (e.g. Domain, realestate.com.au, an agency)",
  "• Sold, for sale or for rent — and the date window",
  "• Filters (price, bedrooms, property type) or “no filters”",
  "• Run now, or a schedule (e.g. “every Monday 6am”)",
].join("\n");

export interface PromptAdapterInfo {
  slug: string;
  name: string;
  supportedStates: readonly string[] | null;
  sourceType: "portal" | "agency";
}

const optionList = (options: readonly { value: string; label: string }[]) =>
  options.map((o) => `${o.value} (${o.label})`).join(", ");

export function buildJobConfigSystemPrompt(ctx: {
  adapters: PromptAdapterInfo[];
  suburbListNames: string[];
  today: string;
}): string {
  const adapters = ctx.adapters
    .map(
      (a) =>
        `- ${a.slug}: ${a.name} [${a.sourceType}; ${a.supportedStates?.join("/") ?? "all states"}]`,
    )
    .join("\n");
  const lists = ctx.suburbListNames.length
    ? ctx.suburbListNames.map((n) => `- ${n}`).join("\n")
    : "(none saved)";

  return `You turn an operator's plain-English description of a property scrape job into a job \
configuration for the Equity Start admin dashboard. Today is ${ctx.today} (Australia).

Output exactly one JSON object matching the provided schema. Never invent facts.

# Never guess
If something you need is missing, ambiguous or contradictory, do not fill it in. Add an entry to \
"unresolved" with the field name and one short, specific question (list the valid options where \
there are some), and leave that part empty or null. A setting listed under Defaults is the only \
thing you may fill in without being told.

# Defaults (use only when the user said nothing about the setting)
Every time you apply one, add {field, value, reason} to "assumedDefaults" so the user can accept \
or override it. Field names are the exact keys below.
- listingType: ${D.listingType}
- dateFilterMode: ${D.dateFilterMode}, timeRange: ${D.timeRange}
- providerMode: ${D.providerMode} (the system picks the scrape provider)
- excludeSurroundingSuburbs: ${D.excludeSurroundingSuburbs} (only the named suburbs, not neighbours)
- excludePriceWithheld: ${D.excludePriceWithheld} (keep every sale; price-withheld sales still \
carry dates and attributes)
- runType: ${D.runType}
- jobName: if the user gives none, write a short descriptive name and record it as a default.
Filters the user did not mention (price, bedrooms, bathrooms, parking, property types) stay null: \
null means no filter and is not a default. Do not record them.

# Sites (adapterSlugs)
Sites are NOT defaulted. If the user does not name the sites, or names one you cannot map \
confidently to a slug below, leave adapterSlugs empty and ask in "unresolved" (field \
"adapterSlugs"), listing the options by name. "All sites" / "every site" means every slug below \
that covers the job's states. Per-site differences go in adapterOverrides (only the fields that \
differ; everything else null). The date window is one per job: set it in globalConfig only.
${adapters}

# Suburbs
Put each suburb in "suburbs" as {locality, postcode, state}. Write the locality as the user did. \
Fill postcode and state only when the user gave them; otherwise null. Do not look up postcodes or \
states yourself — the server matches suburbs against the database and asks the user when a name \
is ambiguous. A saved list goes in suburbListNames, using the exact name from this list:
${lists}
If the user gives neither suburbs nor a list, ask (field "suburbs").

# Values
- listingType: ${optionList(GLOBAL_LISTING_TYPE_OPTIONS)}.
- Relative windows use timeRange: ${optionList(GLOBAL_TIME_RANGE_OPTIONS)}. For a window not in \
that list (e.g. "last 6 months", "since March") use dateFilterMode absolute with dateFrom/dateTo \
(YYYY-MM-DD, dateTo = today unless the user gives an end) and timeRange null. For relative, \
dateFrom/dateTo are null.
- propertyTypes: ${optionList(GLOBAL_PROPERTY_TYPE_OPTIONS)}; null means all.
- Prices are whole AUD (e.g. "1.2m" = 1200000).
- provider is null unless the user forces a specific scrape provider, in which case providerMode \
is forced.
- runType scheduled needs a 5-field cron expression in UTC (Melbourne is UTC+10, or +11 in \
daylight saving — convert). If the user asks for a schedule without a day or time, ask. For \
immediate jobs cronExpression is null.`;
}

export function buildJobConfigUserMessage(
  prompt: string,
  clarifications: { question: string; answer: string }[],
): string {
  const answered = clarifications.filter((c) => c.answer.trim());
  if (!answered.length) return `Job description:\n${prompt.trim()}`;
  const qa = answered.map((c) => `- Q: ${c.question}\n  A: ${c.answer.trim()}`).join("\n");
  return `Job description:\n${prompt.trim()}\n\nAnswers to your earlier questions (these take \
precedence over the description):\n${qa}`;
}
